import { Footer } from '@/app/components/Footer'
import { useCategories } from '@/app/hooks/data-hooks/useCategories'
import { useCourses } from '@/app/hooks/data-hooks/useCourses'
import { useUserCourseRecommendations } from '@/app/hooks/data-hooks/useRecommendations'
import { MAX_COURSE_FILTER_PRICE } from '@/app/lib/constants'
import { SearchInputProvider } from '@/app/modules/course/contexts/InputSearchContext'
import { Header } from '@/app/modules/shared/components/Header'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ContractType } from '@/types/agreement'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { MdOutlineTune } from 'react-icons/md'
import { Button } from '../../shared/components'
import {
  CourseFiltersModal,
  CoursesFilterRow,
  CoursesHeader,
  CoursesList,
} from '../components'
import { NoCoursesMessage } from '../components/NoCoursesMessage'

export const CoursesPage = () => {
  const router = useRouter()
  const t = useTranslations('Courses')
  const [term, setTerm] = useState('')
  const { isAuthenticated } = useAuth()

  const {
    isLoading: isLoadingRecommendedCourses,
    isRefetching: isRefetchingRecommendedCourses,
    data: { data: { courses: recommendedCourses = [] } = {} } = {},
  } = useUserCourseRecommendations({
    enabled: isAuthenticated,
  })

  const { data: { data: categories = [] } = {} } = useCategories(
    {},
    { limit: 500 },
    { retry: false }
  )

  const [isCourseFiltersModalOpen, setCourseFiltersModalOpen] = useState(false)

  const {
    data: { pages: pages = [] } = {},
    fetchNextPage: loadMore,
    hasNextPage: canLoadMore,
    isLoading: isLoadingAllCourses,
    isRefetching: isRefetchingAllCourses,
  } = useCourses(
    {},
    { limit: 8 },
    { qualityScore: 'desc', random: 'asc' },
    { retry: false }
  )

  const filteredCourses = pages.map(({ data }) => data).flat()

  const totalCourses = pages[0]?.metadata.total ?? 0
  const coursesSectionTitle = t('all_courses_in_catalog')

  const clearFilters = () => {
    router.push(router.pathname)
  }

  const redirectToSearchPage = async (data: any) => {
    const mergedData = { ...data }
    if (term) {
      mergedData.term = term
    }

    const query = Object.entries(mergedData).reduce((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        return { ...acc, [key]: value.map((v) => v.id) }
      }

      if (value?.id) {
        return { ...acc, [key]: value.id }
      }

      if (key === 'budget') {
        return {
          ...acc,
          ...(value.min && { minPrice: value.min }),
          ...(value.max &&
            value.max !== MAX_COURSE_FILTER_PRICE && {
              maxPrice: value.max,
            }),
        }
      }

      if (
        key === 'agreementType' &&
        Object.values(ContractType).includes(value)
      ) {
        return {
          ...acc,
          [key]: value,
        }
      }

      if (typeof value === 'string' && value.trim()) {
        return { ...acc, [key]: value.trim() }
      }

      return acc
    }, {})
    await router.push({ pathname: '/search', query })
  }

  const onSearchHandler = async (term: string) => {
    await redirectToSearchPage({ term })
  }

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <SearchInputProvider>
        <Header />
        <main>
          <div className="flex flex-col-reverse sm:flex-col">
            <CoursesHeader
              onFilter={(term) => {
                setCourseFiltersModalOpen(true)
                setTerm(term)
              }}
              className="container"
              onSearch={onSearchHandler}
            />
            <CoursesFilterRow
              categories={[...categories].sort(() => Math.random() - 0.5)}
              className="container"
              wrapperProps={{ className: 'hidden md:flex' }}
            />
          </div>
          <div className="my-10 flex flex-col gap-10 sm:my-14 sm:gap-14">
            {totalCourses === 0 && !isLoadingAllCourses && (
              <NoCoursesMessage onClearFiltersClick={clearFilters} />
            )}
            <CoursesList
              title={
                <SectionTitleWithFiltersButton
                  sectionTitle={coursesSectionTitle}
                  onOpenFiltersModal={() => setCourseFiltersModalOpen(true)}
                />
              }
              courses={filteredCourses}
              loadMore={canLoadMore ? loadMore : undefined}
              className="container"
              isLoading={isLoadingAllCourses || isRefetchingAllCourses}
            />
            <CoursesList
              title={t('tailored_courses')}
              courses={recommendedCourses}
              className="container"
              isLoading={
                isLoadingRecommendedCourses || isRefetchingRecommendedCourses
              }
            />
          </div>
        </main>
        <Footer />
        <CourseFiltersModal
          hideOnClear
          open={isCourseFiltersModalOpen}
          onClose={() => setCourseFiltersModalOpen(false)}
          onSubmit={redirectToSearchPage}
        />
      </SearchInputProvider>
    </>
  )
}

function SectionTitleWithFiltersButton({ sectionTitle, onOpenFiltersModal }) {
  const t = useTranslations('Courses')

  return (
    <div className="flex">
      {sectionTitle}
      <Button
        color="white"
        size="small"
        onClick={onOpenFiltersModal}
        className="ml-auto"
      >
        <div className="flex flex-row items-center gap-2">
          <MdOutlineTune className="h-4 w-4" />
          <span className="hidden text-sm sm:block">{t('filters')}</span>
        </div>
      </Button>
    </div>
  )
}
